import React, { useState, useEffect } from 'react';
import { adminAPI } from '@/lib/api';
import { useAuth } from '@/App';
import { useTranslation } from '@/lib/i18n';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Loader2, BarChart3, CalendarDays, Users, Gift, Tag, CreditCard, Activity, Store, MapPin, Package, Settings, Mail, Globe, Scale, TrendingUp, User, Database, Coins, FileCheck } from 'lucide-react';
import AdminCalendar from '@/components/AdminCalendar';
import { FAVICON_URL } from '@/components/admin/AdminShared';
import { DashboardTab } from '@/components/admin/DashboardTab';
import { BookingsTab } from '@/components/admin/BookingsTab';
import { ActivitiesTab } from '@/components/admin/ActivitiesTab';
import { ResourcesTab } from '@/components/admin/ResourcesTab';
import { ClientsTab } from '@/components/admin/ClientsTab';
import { SitesTab } from '@/components/admin/SitesTab';
import { GiftCardsTab } from '@/components/admin/GiftCardsTab';
import { PromosTab } from '@/components/admin/PromosTab';
import { FlashPromosTab } from '@/components/admin/FlashPromosTab';
import { HomepageTab } from '@/components/admin/HomepageTab';
import { LegalTab } from '@/components/admin/LegalTab';
import { TransactionsTab } from '@/components/admin/TransactionsTab';
import { EmailsTab } from '@/components/admin/EmailsTab';
import { SettingsTab } from '@/components/admin/SettingsTab';
import { AccountTab } from '@/components/admin/AccountTab';
import { BackupTab } from '@/components/admin/BackupTab';
import { CreditsTab } from '@/components/admin/CreditsTab';
import { ContractsTab } from '@/components/admin/ContractsTab';

export default function AdminPage() {
  const { user, updateUser } = useAuth();
  const { t, lang } = useTranslation();
  const isSuperAdmin = user?.role === 'super_admin';
  const isFranchise = user?.role === 'franchise_admin';

  const [stats, setStats] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [activities, setActivities] = useState([]);
  const [sites, setSites] = useState([]);
  const [giftCards, setGiftCards] = useState([]);
  const [promoCodes, setPromoCodes] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [clients, setClients] = useState([]);
  const [resources, setResources] = useState([]);
  const [siteSettings, setSiteSettings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('calendar');
  const [hpSettings, setHpSettings] = useState({});

  // Triggers for cross-component communication
  const [bookingTrigger, setBookingTrigger] = useState(null);
  const [flashPromoTrigger, setFlashPromoTrigger] = useState(null);

  useEffect(() => { loadData(); }, []); // eslint-disable-line

  const loadData = async () => {
    setLoading(true);
    try {
      const promises = [adminAPI.getStats(), adminAPI.getBookings(), adminAPI.getActivities(), adminAPI.getSites(), adminAPI.getTransactions(), adminAPI.getSettings(), adminAPI.getClients(), adminAPI.getResources(), adminAPI.getGiftCards()];
      if (isSuperAdmin) promises.push(adminAPI.getPromoCodes());
      const r = await Promise.all(promises);
      setStats(r[0].data); setBookings(r[1].data); setActivities(r[2].data); setSites(r[3].data);
      setTransactions(r[4].data); setSiteSettings(r[5].data || []); setClients(r[6].data);
      setResources(r[7].data); setGiftCards(r[8].data);
      if (isSuperAdmin) setPromoCodes(r[9].data);
      if (isSuperAdmin) { adminAPI.getHomepageSettings().then(res => setHpSettings(res.data || {})).catch(() => {}); }
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const handleAddBooking = (preDate, preTimeSlot) => {
    setBookingTrigger({ date: preDate || null, time_slot: preTimeSlot || null, ts: Date.now() });
    setActiveTab('bookings');
  };
  const handleFlashPromo = (data) => {
    setFlashPromoTrigger({ ...data, ts: Date.now() });
    setActiveTab('flash');
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A]"><Loader2 className="w-8 h-8 animate-spin text-[#00FF00]" /></div>;

  const tabs = [
    { value: 'dashboard', icon: BarChart3, label: t('admin.dashboard') },
    { value: 'bookings', icon: CalendarDays, label: t('admin.bookings') },
    { value: 'calendar', icon: CalendarDays, label: t('admin.calendar') },
    { value: 'activities', icon: Activity, label: t('admin.activities') },
    { value: 'resources', icon: Package, label: t('admin.resources') },
    { value: 'clients', icon: Users, label: t('admin.clients') },
  ];
  if (isSuperAdmin) { tabs.push({ value: 'sites', icon: MapPin, label: t('admin.sites') }, { value: 'giftcards', icon: Gift, label: t('admin.gift_cards') }, { value: 'credits', icon: Coins, label: 'Forfaits Crédits' }, { value: 'promos', icon: Tag, label: t('admin.promos') }, { value: 'flash', icon: TrendingUp, label: 'Promos flash' }, { value: 'homepage', icon: Globe, label: t('admin.homepage') }, { value: 'legal', icon: Scale, label: 'Pages légales' }, { value: 'backups', icon: Database, label: 'Sauvegardes' }); }
  else { tabs.push({ value: 'giftcards', icon: Gift, label: t('admin.gift_cards') }); }
  tabs.push({ value: 'transactions', icon: CreditCard, label: t('admin.transactions') }, { value: 'emails', icon: Mail, label: 'Emails' }, { value: 'contracts', icon: FileCheck, label: 'Contrats' }, { value: 'settings', icon: Settings, label: t('admin.settings') }, { value: 'account', icon: User, label: 'Mon compte' });

  return (
    <div className="min-h-screen bg-[#0A0A0A]" data-testid="admin-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-8">
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <img src={FAVICON_URL} alt="" className="w-10 h-10 rounded-xl" />
            <div>
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-white" data-testid="admin-title">{t('admin.title')}</h1>
              {isFranchise && <Badge className="bg-blue-500/15 text-blue-400 border-0 text-xs mt-1">{t('admin.franchise')} - {sites[0]?.city || user.site_id}</Badge>}
              {isSuperAdmin && <Badge className="bg-[#00FF00]/15 text-[#00FF00] border-0 text-xs mt-1">{t('admin.super_admin')}</Badge>}
            </div>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="flex flex-wrap gap-1 bg-[#1A1A1A] border border-[#2A2A2A] mb-8 h-auto p-1 overflow-x-auto">
            {tabs.map(tab => (<TabsTrigger key={tab.value} value={tab.value} className="data-[state=active]:bg-[#00FF00] data-[state=active]:text-black text-xs sm:text-sm" data-testid={`admin-tab-${tab.value}`}><tab.icon className="w-4 h-4 mr-1" /> {tab.label}</TabsTrigger>))}
          </TabsList>

          <TabsContent value="dashboard">
            <DashboardTab stats={stats} bookings={bookings} transactions={transactions} clients={clients} sites={sites} isSuperAdmin={isSuperAdmin} setActiveTab={setActiveTab} t={t} />
          </TabsContent>

          <TabsContent value="bookings">
            <BookingsTab bookings={bookings} activities={activities} sites={sites} clients={clients} loadData={loadData} isFranchise={isFranchise} user={user} t={t} lang={lang} bookingTrigger={bookingTrigger} />
          </TabsContent>

          <TabsContent value="calendar">
            <AdminCalendar lang={lang} onAddBooking={handleAddBooking} sites={sites} user={user} onFlashPromo={handleFlashPromo} activities={activities} />
          </TabsContent>

          <TabsContent value="activities">
            <ActivitiesTab activities={activities} sites={sites} resources={resources} loadData={loadData} isFranchise={isFranchise} user={user} t={t} lang={lang} />
          </TabsContent>

          <TabsContent value="resources">
            <ResourcesTab resources={resources} activities={activities} sites={sites} loadData={loadData} isFranchise={isFranchise} user={user} t={t} />
          </TabsContent>

          <TabsContent value="clients">
            <ClientsTab clients={clients} loadData={loadData} activities={activities} />
          </TabsContent>

          {isSuperAdmin && <TabsContent value="sites"><SitesTab sites={sites} loadData={loadData} /></TabsContent>}

          <TabsContent value="giftcards">
            <GiftCardsTab giftCards={giftCards} clients={clients} loadData={loadData} />
          </TabsContent>

          {isSuperAdmin && <TabsContent value="promos"><PromosTab promoCodes={promoCodes} sites={sites} loadData={loadData} t={t} /></TabsContent>}

          {isSuperAdmin && <TabsContent value="flash"><FlashPromosTab sites={sites} activities={activities} user={user} t={t} flashPromoTrigger={flashPromoTrigger} /></TabsContent>}

          {isSuperAdmin && <TabsContent value="homepage"><HomepageTab hpSettings={hpSettings} setHpSettings={setHpSettings} /></TabsContent>}

          {isSuperAdmin && <TabsContent value="legal"><LegalTab /></TabsContent>}

          {isSuperAdmin && <TabsContent value="backups"><BackupTab /></TabsContent>}

          {isSuperAdmin && <TabsContent value="credits"><CreditsTab sites={sites} clients={clients} /></TabsContent>}

          <TabsContent value="transactions">
            <TransactionsTab transactions={transactions} sites={sites} isSuperAdmin={isSuperAdmin} />
          </TabsContent>

          <TabsContent value="emails">
            <EmailsTab sites={sites} isFranchise={isFranchise} user={user} />
          </TabsContent>

          <TabsContent value="contracts">
            <ContractsTab bookings={bookings} />
          </TabsContent>

          <TabsContent value="settings">
            <SettingsTab sites={sites} siteSettings={siteSettings} setSiteSettings={setSiteSettings} />
          </TabsContent>

          <TabsContent value="account">
            <AccountTab user={user} updateUser={updateUser} isSuperAdmin={isSuperAdmin} sites={sites} />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
